// Wiki seed data — categorized pages with mock metadata + HTML content.
// Modeled on Pulse_Base's SOP template structure (Goal · Assets · Location
// · Tools · Deliverable · Measure of Success · Instructions · Special
// Notes) so the demo content looks deliberately structured.

const daysAgo = (n) => new Date(Date.now() - n * 86_400_000).toISOString();

export const WIKI_CATEGORIES = [
  { id: 'sops',      name: 'SOPs',      icon: 'BookOpen',     description: 'Standard Operating Procedures' },
  { id: 'training',  name: 'Training',  icon: 'GraduationCap', description: 'New-hire and ongoing training' },
  { id: 'policies',  name: 'Policies',  icon: 'Scale',        description: 'Company-wide policies' },
  { id: 'protocols', name: 'Protocols', icon: 'AlertTriangle', description: 'Incident response and operational protocols' },
];

const SOP_BODY = (goal, assets, location, tools, deliverable, measure, instructions) => `
<h2>Goal</h2><p>${goal}</p>
<h2>Assets</h2><p>${assets}</p>
<h2>Location</h2><p>${location}</p>
<h2>Tools</h2><p>${tools}</p>
<h2>Deliverable</h2><p>${deliverable}</p>
<h2>Measure of Success</h2><p>${measure}</p>
<h2>Instructions</h2>${instructions}
`.trim();

export const WIKI_PAGES = [
  // ── SOPs ───────────────────────────────────────────────────────────────
  {
    id: 'sop_morning_cash',
    title: 'Morning Routine — Cash Register',
    category: 'sops',
    type: 'sop',
    owner: { name: 'Manzil Zaheer', initials: 'MZ' },
    tags: ['morning-routine', 'cash', 'pos'],
    updatedAt: daysAgo(3),
    verified: true,
    body: SOP_BODY(
      'Ensure the register opens with the correct float and is reconciled before customer-facing hours.',
      'Cash drawer, opening-float log, secure key.',
      'Front counter — primary register.',
      'POS terminal, manager key, opening-float envelope.',
      'Reconciled register, signed log entry, system ready for sale.',
      'Float matches expected amount within $0.50, log entry submitted before 9:00 AM.',
      '<ol><li>Retrieve opening-float envelope from safe.</li><li>Count cash twice; record in log.</li><li>Enter opening balance in POS.</li><li>Print cash-drawer report; staple to log.</li><li>Sign and date entry.</li></ol>',
    ),
  },
  {
    id: 'sop_evening_close',
    title: 'Evening Routine — POS Close-Out',
    category: 'sops',
    type: 'sop',
    owner: { name: 'Manzil Zaheer', initials: 'MZ' },
    tags: ['evening-routine', 'cash', 'pos'],
    updatedAt: daysAgo(5),
    verified: true,
    body: SOP_BODY(
      'Close the register cleanly: reconcile cash, post the daily report, secure the cash bag.',
      'End-of-day sales report, deposit slip, locked deposit bag.',
      'Back office.',
      'POS terminal, manager key, deposit envelope, safe.',
      'Closed register, daily report filed, deposit bag in safe.',
      'Variance under $1.00; deposit bag in safe by 9:30 PM.',
      '<ol><li>Run end-of-day report on POS.</li><li>Reconcile cash to report.</li><li>Prepare deposit slip; seal envelope.</li><li>Place in safe; log timestamp.</li></ol>',
    ),
  },
  {
    id: 'sop_inventory_audit',
    title: 'Monthly Inventory Audit',
    category: 'sops',
    type: 'sop',
    owner: { name: 'Priya Shah', initials: 'PS' },
    tags: ['inventory', 'store', 'seasonal'],
    updatedAt: daysAgo(12),
    verified: true,
    body: SOP_BODY(
      'Verify physical inventory matches system records; identify shrinkage and reorder thresholds.',
      'Cycle-count sheets, barcode scanner, system inventory export.',
      'Stockroom + display floor.',
      'Scanner, clipboard, system access on tablet.',
      'Cycle-count sheet with variances flagged, signed by manager.',
      'Variance under 2% by SKU; flagged items resolved within 48 hours.',
      '<ol><li>Print cycle-count sheet by section.</li><li>Scan or count each SKU.</li><li>Note variances in red.</li><li>Submit signed sheet by EOD.</li></ol>',
    ),
  },
  {
    id: 'sop_vendor_onboard',
    title: 'Vendor Onboarding',
    category: 'sops',
    type: 'sop',
    owner: { name: 'Rachel Kim', initials: 'RK' },
    tags: ['vendors'],
    updatedAt: daysAgo(8),
    verified: false,
    body: SOP_BODY(
      'Bring a new vendor into the system: contract, terms, payment, and recurring schedule.',
      'Vendor packet (W-9, insurance cert, payment terms), system vendor record.',
      'Accounting back office.',
      'Vendor portal access, AP system, QuickBooks.',
      'Complete vendor record with active payment terms.',
      'First invoice processable within 24 hours of vendor activation.',
      '<ol><li>Collect W-9 + insurance cert.</li><li>Create vendor in QuickBooks.</li><li>Set payment terms (default Net 30).</li><li>Enter recurring expense schedule.</li></ol>',
    ),
  },

  // ── Training ───────────────────────────────────────────────────────────
  {
    id: 'train_new_hire',
    title: 'New Hire Orientation',
    category: 'training',
    type: 'training',
    owner: { name: 'Aisha Patel', initials: 'AP' },
    tags: ['hiring', 'training'],
    updatedAt: daysAgo(20),
    verified: true,
    body: `
<h2>Welcome</h2><p>Your first week sets the foundation. Expect structured onboarding across systems, culture, and role-specific skills.</p>
<h2>Day 1</h2><ul><li>Workspace setup, badge, parking</li><li>Org & team intro</li><li>Tooling: ClickUp, Slack, QuickBooks (read-only at first)</li></ul>
<h2>Day 2–5</h2><ul><li>Role-shadow with manager</li><li>Customer-service standards (see Training: Customer Service)</li><li>Safety walk-through (see Training: Safety)</li></ul>
<h2>Week 2 Check-in</h2><p>30-minute manager check-in. Cover: questions, blockers, role expectations.</p>
    `.trim(),
  },
  {
    id: 'train_customer_service',
    title: 'Customer Service Standards',
    category: 'training',
    type: 'training',
    owner: { name: 'Aisha Patel', initials: 'AP' },
    tags: ['training', 'customer-site'],
    updatedAt: daysAgo(18),
    verified: true,
    body: `
<h2>Greet, Listen, Resolve</h2><p>Every customer interaction follows the same shape — greet within 10 seconds, listen without interrupting, resolve or escalate.</p>
<h2>Greeting</h2><ul><li>Make eye contact</li><li>Open with: "How can I help you today?"</li><li>Use the customer's name if available</li></ul>
<h2>Listening</h2><p>Confirm understanding before proposing a solution. Restate the issue in one sentence.</p>
<h2>Resolving</h2><p>Most issues fit one of three buckets: information, exchange, escalation. If you can't resolve in 5 minutes, escalate to a manager.</p>
    `.trim(),
  },
  {
    id: 'train_safety',
    title: 'Safety Training',
    category: 'training',
    type: 'training',
    owner: { name: 'David Liu', initials: 'DL' },
    tags: ['safety', 'training'],
    updatedAt: daysAgo(30),
    verified: true,
    body: `
<h2>General Safety</h2><p>Know where exits, fire extinguishers, and first-aid kits are. Two-week refresher quiz.</p>
<h2>Lifting & Carrying</h2><ul><li>Lift with legs, not back</li><li>Anything over 50 lb requires a partner</li><li>Use the hand truck for large boxes</li></ul>
<h2>Reporting</h2><p>Any incident — no matter how minor — goes in the incident log within the hour.</p>
    `.trim(),
  },

  // ── Policies ───────────────────────────────────────────────────────────
  {
    id: 'pol_pto',
    title: 'Time Off & PTO Policy',
    category: 'policies',
    type: 'policy',
    owner: { name: 'Rachel Kim', initials: 'RK' },
    tags: [],
    updatedAt: daysAgo(60),
    verified: true,
    body: `
<h2>Accrual</h2><p>Full-time employees accrue 1.5 days/month, capped at 25 days. Carryover up to 5 days/year.</p>
<h2>Requesting</h2><p>Submit through the HR portal at least 2 weeks ahead for periods over 3 days. Same-week requests are case-by-case.</p>
<h2>Sick Leave</h2><p>Separate from PTO. 8 days/year, no carryover.</p>
    `.trim(),
  },
  {
    id: 'pol_expense',
    title: 'Expense Reimbursement Policy',
    category: 'policies',
    type: 'policy',
    owner: { name: 'Rachel Kim', initials: 'RK' },
    tags: [],
    updatedAt: daysAgo(45),
    verified: true,
    body: `
<h2>Eligible Expenses</h2><ul><li>Mileage at IRS rate</li><li>Client meals (with itemized receipt and attendees)</li><li>Pre-approved subscriptions and tools</li></ul>
<h2>Submission</h2><p>Submit within 30 days. Receipts required for anything over $25.</p>
<h2>Reimbursement</h2><p>Paid on the next regular payroll after approval (typically 1–2 weeks).</p>
    `.trim(),
  },
  {
    id: 'pol_conduct',
    title: 'Code of Conduct',
    category: 'policies',
    type: 'policy',
    owner: { name: 'Aisha Patel', initials: 'AP' },
    tags: [],
    updatedAt: daysAgo(90),
    verified: true,
    body: `
<h2>Core Principles</h2><ul><li>Honesty in every interaction</li><li>Respect across roles and backgrounds</li><li>Confidentiality of customer and company data</li></ul>
<h2>Reporting Concerns</h2><p>Issues can be raised confidentially to HR or any manager. No retaliation policy in effect.</p>
    `.trim(),
  },

  // ── Protocols ─────────────────────────────────────────────────────────
  {
    id: 'proto_incident',
    title: 'Incident Response',
    category: 'protocols',
    type: 'protocol',
    owner: { name: 'David Liu', initials: 'DL' },
    tags: ['safety'],
    updatedAt: daysAgo(15),
    verified: true,
    body: `
<h2>Severity Levels</h2><ul><li><strong>P0</strong> — injury or imminent risk. Call 911 first, manager second.</li><li><strong>P1</strong> — equipment failure stopping operations. Page on-call manager.</li><li><strong>P2</strong> — minor incident. Log within 1 hour.</li></ul>
<h2>Communication</h2><p>For P0/P1, post in #ops-urgent. Update every 30 minutes until resolved.</p>
    `.trim(),
  },
  {
    id: 'proto_equipment',
    title: 'Equipment Maintenance',
    category: 'protocols',
    type: 'protocol',
    owner: { name: 'Priya Shah', initials: 'PS' },
    tags: ['installation'],
    updatedAt: daysAgo(22),
    verified: false,
    body: `
<h2>Monthly Checks</h2><p>First Monday of each month — visual inspection of all production equipment, log condition and any unusual wear.</p>
<h2>Quarterly Service</h2><p>Schedule vendor service for compressors, lifts, and HVAC. Confirm appointment by the 1st of the quarter.</p>
<h2>Repair Triage</h2><p>Operational impact > 1 day → escalate to VP Ops. Otherwise route through the maintenance ticket queue.</p>
    `.trim(),
  },
  {
    id: 'proto_seasonal',
    title: 'Seasonal Setup',
    category: 'protocols',
    type: 'protocol',
    owner: { name: 'Priya Shah', initials: 'PS' },
    tags: ['seasonal', 'store'],
    updatedAt: daysAgo(28),
    verified: true,
    body: `
<h2>Spring</h2><p>Inventory reset, refresh displays, seasonal supplier orders by Feb 15.</p>
<h2>Summer</h2><p>Extended hours posted by May 1; staffing plan to HR by Apr 15.</p>
<h2>Fall</h2><p>Back-to-school promo planning by Aug 1.</p>
<h2>Winter</h2><p>Holiday hours and PTO blackout dates posted by Oct 15.</p>
    `.trim(),
  },
];
